import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ActivationFunnelService } from '@/services/analytics/ActivationFunnelService';
import { AssetGeneratorService } from '@/services/activation/AssetGeneratorService';
import { ActivationPackAssetStorageService } from '@/services/activation';
import {
  assertCompleteActivationAssets,
  downloadAssetsAsZip,
  getExpectedActivationAssetTypes,
  getMissingDefaultPromoVariants,
} from '@/services/activation/zip-bundler';
import {
  ActivationPackWithTarget,
  ASSET_TYPE_INFO,
  GeneratedAsset,
  GeneratedAssetCache,
  GeneratedAssetType,
} from '@/types/activation/activation-pack';
import { QRCodeStorageService } from '@/services/qr-code/QRCodeStorageService';
import { ActivationPackService } from '@/services/database/ActivationPackService';
import { productManualUrl } from '@/services/marketplace/ProductGenieService';
import { DEFAULT_QR_DISPLAY_CONFIG, QRDisplayConfig } from '@/types/ui/branding';

// Persisting generated_assets updates the pack row a moment after each asset's
// generated_at timestamp. This grace window avoids treating that same write as
// stale while still invalidating caches made before a later pack edit.
const CACHE_WRITE_GRACE_MS = 10_000;

interface UseAssetGeneratorReturn {
  isGenerating: boolean;
  error: string | null;
  generateAsset: (type: GeneratedAssetType) => Promise<GeneratedAsset | null>;
  generateAllAssets: () => Promise<GeneratedAsset[]>;
  getCachedAssets: () => GeneratedAsset[];
  persistAssets: (assets: GeneratedAsset[]) => Promise<GeneratedAssetCache>;
  /** Resolve a single asset from the persisted cache, else generate it fresh. */
  resolveAsset: (type: GeneratedAssetType) => Promise<GeneratedAsset | null>;
  downloadAsset: (asset: GeneratedAsset) => Promise<void>;
  /** Resolve (cache-first) then download a single asset by type. */
  downloadByType: (type: GeneratedAssetType) => Promise<void>;
  downloadAllAssets: () => Promise<GeneratedAsset[]>;
}

/**
 * Asset uploads are serial, followed by one pack-row update. Treat the whole
 * cache as that same write when its newest entry is close to `updated_at`, so
 * an early upload in a long build is not immediately considered stale.
 */
export const isCachedActivationAssetFresh = (
  packUpdatedAt: string,
  cache: GeneratedAssetCache,
  type: GeneratedAssetType,
): boolean => {
  const cachedAt = Date.parse(cache[type]?.generated_at || '');
  if (!Number.isFinite(cachedAt)) return false;

  const updatedAt = Date.parse(packUpdatedAt);
  if (!Number.isFinite(updatedAt)) return true;
  if (updatedAt - cachedAt <= CACHE_WRITE_GRACE_MS) return true;

  const newestCachedAt = Math.max(
    ...Object.values(cache)
      .map((entry) => Date.parse(entry?.generated_at || ''))
      .filter(Number.isFinite),
  );

  return Number.isFinite(newestCachedAt) && updatedAt - newestCachedAt <= CACHE_WRITE_GRACE_MS;
};

export const useAssetGenerator = (pack: ActivationPackWithTarget | null): UseAssetGeneratorReturn => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  /** Persist a QR-type asset to storage (fire-and-forget). */
  const persistQRAsset = useCallback(
    (asset: GeneratedAsset) => {
      const agentId = pack?.agent_id || pack?.agent?.id;
      if (!agentId) return;

      const isGenieQR = asset.type === 'qr_png' || asset.type === 'qr_svg';
      const isProductQR = asset.type === 'product_qr_png' || asset.type === 'product_qr_svg';
      if (!isGenieQR && !isProductQR) return;

      const format = asset.type.endsWith('_png') ? 'png' as const : 'svg' as const;
      const productSlug = pack?.asset_config.product_slug;
      const targetUrl = isProductQR
        ? (productSlug ? productManualUrl(productSlug) : '')
        : pack ? ActivationPackService.getTargetUrl(pack) : '';
      if (!targetUrl) return;
      const config: QRDisplayConfig = {
        ...DEFAULT_QR_DISPLAY_CONFIG,
        style: pack?.asset_config.qr_style || 'framed',
        label: pack?.asset_config.qr_label || 'scan-to-connect',
        showLogo: pack?.asset_config.qr_include_logo ?? true,
      };

      // Convert asset data to blob
      let blobPromise: Promise<Blob>;
      if (asset.data instanceof Blob) {
        blobPromise = Promise.resolve(asset.data);
      } else if (typeof asset.data === 'string' && asset.data.startsWith('data:')) {
        blobPromise = fetch(asset.data).then((r) => r.blob());
      } else if (typeof asset.data === 'string') {
        blobPromise = Promise.resolve(new Blob([asset.data], { type: asset.mimeType }));
      } else {
        return;
      }

      blobPromise
        .then((blob) =>
          QRCodeStorageService.saveQRCode(
            { agent_id: agentId, format, display_config: config, target_url: targetUrl, blob, filename: asset.filename }
          )
        )
        .catch((err) => console.error('Failed to save activation pack QR to storage:', err));
    },
    [pack]
  );

  const generateAsset = useCallback(
    async (type: GeneratedAssetType): Promise<GeneratedAsset | null> => {
      if (!pack) {
        setError('No activation pack provided');
        return null;
      }

      setIsGenerating(true);
      setError(null);

      try {
        let asset: GeneratedAsset;

        switch (type) {
          case 'qr_png':
            asset = await AssetGeneratorService.generateQRPng(pack);
            break;
          case 'qr_svg':
            asset = await AssetGeneratorService.generateQRSvg(pack);
            break;
          case 'product_qr_png':
            asset = await AssetGeneratorService.generateProductQRPng(pack);
            break;
          case 'product_qr_svg':
            asset = await AssetGeneratorService.generateProductQRSvg(pack);
            break;
          case 'sticker_pdf':
            asset = await AssetGeneratorService.generateStickerPdf(pack);
            break;
          case 'email_signature_html':
            asset = AssetGeneratorService.generateEmailSignature(pack);
            break;
          case 'social_card_png':
            asset = await AssetGeneratorService.generateSocialCard(pack);
            break;
          case 'embed_code':
            asset = AssetGeneratorService.generateEmbedCode(pack);
            break;
          case 'lookbook_pdf':
            asset = await AssetGeneratorService.generateLookbookPdf(pack);
            break;
          case 'poster_pack_pdf':
            asset = await AssetGeneratorService.generatePosterPackPdf(pack);
            break;
          case 'app_poster_png':
            asset = await AssetGeneratorService.generateAppInstallPoster(pack, 'png');
            break;
          case 'app_poster_pdf':
            asset = await AssetGeneratorService.generateAppInstallPoster(pack, 'pdf');
            break;
          default:
            throw new Error(`Unsupported asset type: ${type}`);
        }

        return asset;
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Failed to generate asset';
        setError(message);
        return null;
      } finally {
        setIsGenerating(false);
      }
    },
    [pack]
  );

  const generateAllAssets = useCallback(async (): Promise<GeneratedAsset[]> => {
    if (!pack) {
      setError('No activation pack provided');
      return [];
    }

    setIsGenerating(true);
    setError(null);

    try {
      const assets = await AssetGeneratorService.generateAllAssets(pack);
      return assets;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to generate assets';
      setError(message);
      return [];
    } finally {
      setIsGenerating(false);
    }
  }, [pack]);

  const getCachedAssets = useCallback((): GeneratedAsset[] => {
    const cache = pack?.generated_assets || {};
    return (Object.keys(cache) as GeneratedAssetType[])
      .filter((type) => cache[type] && ASSET_TYPE_INFO[type])
      .map((type) => {
        const entry = cache[type]!;
        const meta = ASSET_TYPE_INFO[type];
        return ActivationPackAssetStorageService.cacheToAsset(
          type,
          entry,
          meta.label,
          meta.description,
        );
      });
  }, [pack]);

  const persistAssets = useCallback(
    async (assets: GeneratedAsset[]): Promise<GeneratedAssetCache> => {
      if (!pack) {
        const persistenceError = new Error('No activation pack provided');
        setError(persistenceError.message);
        throw persistenceError;
      }
      try {
        return await ActivationPackAssetStorageService.buildAndPersist(pack, assets);
      } finally {
        // Also refetch after an ambiguous network failure: the CAS may have
        // committed before its response was lost, and the accepted revision is
        // the authoritative recovery signal.
        await queryClient.invalidateQueries({ queryKey: ['activation-pack', pack.id] });
        await queryClient.invalidateQueries({ queryKey: ['print-order', pack.id] });
      }
    },
    [pack, queryClient],
  );

  const downloadAsset = useCallback(async (asset: GeneratedAsset): Promise<void> => {
    setError(null);
    try {
      let url: string;
      let createdObjectUrl = false;
      let assetToPersist = asset;

      if (asset.data instanceof Blob) {
        url = URL.createObjectURL(asset.data);
        createdObjectUrl = true;
      } else if (typeof asset.data === 'string') {
        if (asset.data.startsWith('data:')) {
          // Data URL — use directly
          url = asset.data;
        } else if (/^https?:\/\//i.test(asset.data)) {
          // Persisted storage URL — fetch and force download so the browser
          // honours the filename instead of opening the asset inline.
          const response = await fetch(asset.data);
          if (!response.ok) {
            throw new Error(`Failed to download ${asset.filename} (${response.status})`);
          }
          const blob = await response.blob();
          if (blob.size === 0) {
            throw new Error(`Failed to download ${asset.filename}: the file was empty`);
          }
          url = URL.createObjectURL(blob);
          createdObjectUrl = true;
          assetToPersist = { ...asset, data: blob };
        } else {
          // Inline text content (HTML, SVG)
          const blob = new Blob([asset.data], { type: asset.mimeType });
          url = URL.createObjectURL(blob);
          createdObjectUrl = true;
        }
      } else {
        throw new Error('Invalid asset data type');
      }

      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = asset.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } finally {
        if (createdObjectUrl) {
          URL.revokeObjectURL(url);
        }
      }

      persistQRAsset(assetToPersist);

      // Activation funnel — the pack left the app as a file. Emitted here
      // rather than at each button because every single-asset download in the
      // app routes through this function.
      ActivationFunnelService.packShared(
        ActivationFunnelService.packGenieId(pack),
        'download',
        { asset_type: asset.type },
      );
    } catch (error) {
      console.error('Failed to download asset:', error);
      const downloadError = error instanceof Error
        ? error
        : new Error('Failed to download asset');
      setError(downloadError.message);
      throw downloadError;
    }
  }, [pack, persistQRAsset]);

  const resolveAsset = useCallback(
    async (type: GeneratedAssetType): Promise<GeneratedAsset | null> => {
      if (!pack) return null;
      const cached = pack.generated_assets?.[type];
      const shouldRefreshInlineAsset =
        type === 'email_signature_html' || type === 'embed_code';
      const isCachedAssetStale = cached
        ? !isCachedActivationAssetFresh(pack.updated_at, pack.generated_assets || {}, type)
        : false;

      // Inline snippets are cheap to rebuild and may contain canonical URLs or
      // markup that changed after a pack was first cached.
      if (
        cached &&
        ASSET_TYPE_INFO[type] &&
        !shouldRefreshInlineAsset &&
        !isCachedAssetStale
      ) {
        const meta = ASSET_TYPE_INFO[type];
        return ActivationPackAssetStorageService.cacheToAsset(
          type,
          cached,
          meta.label,
          meta.description,
        );
      }
      return generateAsset(type);
    },
    [pack, generateAsset],
  );

  const downloadByType = useCallback(
    async (type: GeneratedAssetType) => {
      const asset = await resolveAsset(type);
      if (!asset) {
        const downloadError = new Error(`Failed to prepare ${ASSET_TYPE_INFO[type].label}`);
        setError(downloadError.message);
        throw downloadError;
      }
      await downloadAsset(asset);
    },
    [resolveAsset, downloadAsset],
  );

  const downloadAllAssets = useCallback(async (): Promise<GeneratedAsset[]> => {
    if (!pack) {
      const message = 'No activation pack provided';
      setError(message);
      throw new Error(message);
    }

    setIsGenerating(true);
    setError(null);

    try {
      let bundlePack = pack;
      const missingPromoVariants = getMissingDefaultPromoVariants(pack);
      if (missingPromoVariants.length > 0) {
        const targetUrl = ActivationPackService.getTargetUrl(pack);
        const generatedVariants = await Promise.all(
          missingPromoVariants.map(async ({ template, tone, label }) => {
            const result = await ActivationPackService.generatePromo(
              pack.id,
              template,
              tone,
              targetUrl,
            );
            if (
              !result ||
              result.template !== template ||
              result.tone !== tone ||
              !result.content?.body?.trim()
            ) {
              throw new Error(`Failed to generate ${label} promo copy`);
            }
            return result;
          }),
        );

        const promoContent = { ...pack.promo_content };
        for (const result of generatedVariants) {
          promoContent[result.template] = {
            ...(promoContent[result.template] || {}),
            [result.tone]: result.content,
          };
        }
        bundlePack = { ...pack, promo_content: promoContent };
        await queryClient.invalidateQueries({ queryKey: ['activation-pack', pack.id] });
      }

      const expectedAssetTypes = getExpectedActivationAssetTypes(pack.touchpoints, pack.asset_config);
      const assets: GeneratedAsset[] = [];

      // Resolve sequentially because several print assets render against the DOM.
      // Running those canvas jobs together is both slower and less reliable.
      for (const type of expectedAssetTypes) {
        const asset = await resolveAsset(type);
        if (asset) assets.push(asset);
      }

      assertCompleteActivationAssets(assets, expectedAssetTypes);

      const zipName = `${pack.name || 'activation-pack'}.zip`;
      await downloadAssetsAsZip(assets, zipName, {
        pack: bundlePack,
        presentationUrl: ActivationPackService.getPresentationUrl(bundlePack),
        smartLinkUrl: ActivationPackService.getAppSmartLinkUrl(bundlePack),
        expectedAssetTypes,
      });

      // Activation funnel — the whole pack left as one zip. The bundle path
      // builds its own archive rather than calling `downloadAsset`, so it
      // needs its own emit.
      ActivationFunnelService.packShared(
        ActivationFunnelService.packGenieId(pack),
        'download',
        { asset_type: 'bundle_zip' },
      );
      return assets;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to download assets';
      setError(message);
      throw e instanceof Error ? e : new Error(message);
    } finally {
      setIsGenerating(false);
    }
  }, [pack, queryClient, resolveAsset]);

  return {
    isGenerating,
    error,
    generateAsset,
    generateAllAssets,
    getCachedAssets,
    persistAssets,
    resolveAsset,
    downloadAsset,
    downloadByType,
    downloadAllAssets,
  };
};
